import React from 'react';

import * as yup from 'yup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams, useNavigate } from 'react-router-dom';

import defaultClient from '@src/api/clients/default';
import { FormBase, Submit, TextField } from '@src/components/form';
import { useMessage } from '@src/context';
import useForm from '@src/hooks/useForm';
import leadGurusLogo from '@src/assets/logo-full-dark.png';

const schema = yup.object().shape({
  new_password1: yup.string().min(8, 'Use at least 8 characters').required('Password is required'),
  new_password2: yup
    .string()
    .oneOf([yup.ref('new_password1'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

export default function AccountSetup() {
  const [params] = useSearchParams();
  const message = useMessage();
  const navigate = useNavigate();

  const uid = params.get('uid');
  const token = params.get('token');

  const form = useForm({
    schema,
    defaultValues: { new_password1: '', new_password2: '' },
    resetAfterSuccessSubmit: true,
  });

  const [done, setDone] = React.useState(false);

  if (!uid || !token) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>Invalid Setup Link</Typography>
            <Typography color="text.secondary">
              This link is invalid or has expired. Please check your email for a valid invite link.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Account Created!</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your password has been set. You can now sign in to the Lead Gurus portal.
            </Typography>
            <Submit variant="contained" onClick={() => navigate('/auth/login')} sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}>
              Sign In
            </Submit>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleSubmit = (payload) =>
    defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/password/reset/confirm/',
        method: 'POST',
        payload: { ...payload, uid, token },
      })
      .then(() => {
        setDone(true);
      })
      .catch(() => {
        message.error('Unable to create your account. The link may be invalid or expired.');
      });

  return (
    <Box sx={{ maxWidth: 480, width: '100%' }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28, marginBottom: 16 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Set Up Your Account</Typography>
            <Typography color="text.secondary">
              Create a password to access your Lead Gurus client portal.
            </Typography>
          </Box>
          <FormBase onSubmit={form.handleSubmit({ onSubmit: handleSubmit })}>
            <Stack spacing={2.5}>
              <TextField
                control={form.control}
                fullWidth
                label="Create password"
                name="new_password1"
                type="password"
                autoComplete="new-password"
              />
              <TextField
                control={form.control}
                fullWidth
                label="Confirm password"
                name="new_password2"
                type="password"
                autoComplete="new-password"
              />
              <Submit
                variant="contained"
                fullWidth
                sx={{ py: 1.5, bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' }, fontWeight: 600 }}
              >
                Create Account
              </Submit>
            </Stack>
          </FormBase>
        </CardContent>
      </Card>
    </Box>
  );
}

AccountSetup.displayName = 'AccountSetup';
